// src/components/GovernancePortalPage.jsx
import React, { useState, useEffect } from 'react';
import { ethers } from 'ethers';
import { useWeb3 } from '../hooks/useWeb3';
import TransactionResultPopup from '../components/TransactionResultPopup';

const GovernancePortalPage = () => {
  const web3 = useWeb3();
  const { 
    provider, signer, account, contract,
    governanceContract, yildxTokenContract,
    isLoading: web3Loading, error: web3Error, 
    connect 
  } = web3;
  
  // State variables
  const [proposals, setProposals] = useState([]);
  const [activeProposals, setActiveProposals] = useState([]);
  const [pastProposals, setPastProposals] = useState([]);
  const [selectedProposal, setSelectedProposal] = useState(null);
  const [showProposalDetails, setShowProposalDetails] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [votingPower, setVotingPower] = useState('0');
  const [yildxBalance, setYildxBalance] = useState('0');
  const [delegatedTo, setDelegatedTo] = useState(null);
  const [delegators, setDelegators] = useState([]);
  const [operatorInfo, setOperatorInfo] = useState(null);
  const [autoApprovalStatuses, setAutoApprovalStatuses] = useState({});
  const [currentView, setCurrentView] = useState('active');
  const [activeTab, setActiveTab] = useState('active');
  
  // Transaction popup state
  const [showTxPopup, setShowTxPopup] = useState(false);
  const [txStatus, setTxStatus] = useState('pending'); // 'pending', 'success', 'failed', 'cancelled'
  const [txHash, setTxHash] = useState('');
  const [txMessage, setTxMessage] = useState('');
  const [txProvider, setTxProvider] = useState(null);
  
  // Search and filter state
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');

  /**
   * Safely converts BigNumber to a JavaScript number without overflow errors
   * For large numbers, it returns the formatted string instead
   */
  const safeNumberConversion = (bigNumber) => {
    if (!bigNumber) return 0;
    
    try {
      // For small numbers, convert to actual number
      return bigNumber.toNumber();
    } catch (error) {
      if (error.code === "NUMERIC_FAULT" && error.fault === "overflow") {
        // For large numbers that would overflow, return formatted string
        return ethers.utils.formatEther(bigNumber);
      }
      throw error;
    }
  };

  // Auto-connect on component mount
  useEffect(() => {
    const attemptAutoConnection = async () => {
      // Check if we have a saved account in localStorage
      const savedAccount = localStorage.getItem('connected_account');
      
      if (savedAccount && !account) {
        console.log("GovernancePortal: Found saved account, attempting auto-connect...");
        try {
          await connect();
        } catch (error) {
          console.error("GovernancePortal: Auto-connect failed:", error);
        }
      }
    };
    
    attemptAutoConnection();
  }, [connect, account]);
  
  // Make sure to grab the provider if available
  useEffect(() => {
    if (yildxTokenContract && yildxTokenContract.provider) {
      setTxProvider(yildxTokenContract.provider);
    }
  }, [yildxTokenContract]);
  
  // Load proposals and user data
  useEffect(() => {
    if (governanceContract && yildxTokenContract && account) {
      loadProposals();
      loadUserGovernanceData();
      checkDelegationStatus();
      checkOperatorStatus();
    }
  }, [governanceContract, yildxTokenContract, account, contract]);
  
  // Check if user is operator
  const checkOperatorStatus = async () => {
    if (!contract || !account) return;
    
    try {
      // Check if the current account is an operator
      const isOperator = await contract.authorizedOperators(account);
      if (isOperator) {
        setOperatorInfo({
          address: account,
          isCurrentUser: true
        });
      }
    } catch (error) {
      console.error("Error checking operator status:", error);
    }
  };
  
  // Check if user is delegating or has delegators
  const checkDelegationStatus = async () => {
    try {
      if (!governanceContract || !account) return;
      
      // Check if user is delegating to anyone
      try {
        const delegatee = await governanceContract.delegations(account);
        if (delegatee && delegatee !== ethers.constants.AddressZero) {
          setDelegatedTo(delegatee);
        } else {
          setDelegatedTo(null);
        }
      } catch (error) {
        console.error("Error checking delegation status:", error);
        setDelegatedTo(null);
      }
      
      // Check if user has any delegators
      try {
        const userDelegators = await governanceContract.getDelegators(account);
        if (userDelegators && userDelegators.length > 0) {
          setDelegators(userDelegators);
        } else {
          setDelegators([]);
        }
      } catch (error) {
        console.error("Error fetching delegators:", error);
        setDelegators([]);
      }
    } catch (error) {
      console.error("Error in checkDelegationStatus:", error);
    }
  };
  
  // Reset state before loading proposals
  const resetState = () => {
    setProposals([]);
    setActiveProposals([]);
    setPastProposals([]);
    setAutoApprovalStatuses({});
  };
  
  // Comprehensive reload function with delays to ensure state is updated
  const reloadProposals = async () => {
    // Reset all proposal-related state
    resetState();
    
    // Show loading indicator
    setLoading(true);
    
    // Small delay to ensure any blockchain state changes are visible
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    // Load proposals
    await loadProposals();
    
    // Force UI refresh if needed by briefly toggling the view
    if (currentView) {
      const tempView = currentView;
      setCurrentView(null);
      await new Promise(resolve => setTimeout(resolve, 100));
      setCurrentView(tempView);
    }
    
    // Hide loading indicator
    setLoading(false);
  };
  
  // Helper function to safely convert blockchain timestamp to JS Date
  const blockchainTimeToDate = (timestamp) => {
    // If it's already a Date object, return it
    if (timestamp instanceof Date) return timestamp;
    
    // If it's a BigNumber, convert to number
    if (timestamp._isBigNumber) {
      timestamp = timestamp.toNumber();
    }
    
    // Ensure timestamp is treated as seconds (blockchain standard)
    // and convert to milliseconds for JS Date
    return new Date(timestamp * 1000);
  };
  
  // Load all proposals
  const loadProposals = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Get proposal count
      const proposalCount = await governanceContract.proposalCount();
      console.log(`Found ${proposalCount} proposals`);
      
      const proposalList = [];
      const newAutoApprovalStatuses = {};
      
      // Get current date for comparison
      const currentDate = new Date();
      console.log("Current date for comparison:", currentDate);
      
      // Load each proposal
      for (let i = 0; i < proposalCount; i++) {
        try {
          const proposal = await governanceContract.getProposal(i);
          
          // Convert blockchain timestamps to JS Dates
          const creationTime = blockchainTimeToDate(proposal.creationTime);
          const votingEndTime = blockchainTimeToDate(proposal.votingEndTime);
          
          // Log proposal details for debugging
          console.log(`Proposal #${i} details:`, {
            id: i,
            proposer: proposal.proposer,
            proposalType: proposal.proposalType,
            description: proposal.description,
            creationTime,
            votingEndTime,
            votesFor: ethers.utils.formatEther(proposal.votesFor),
            votesAgainst: ethers.utils.formatEther(proposal.votesAgainst),
            executed: proposal.executed,
            canceled: proposal.canceled,
            canExecute: proposal.canExecute
          });
          
          // Get user vote status
          const [hasVoted, support, weight] = await governanceContract.hasVoted(i, account);
          
          // Get auto-approval status
          let autoApprovalStatus = { canAutoApprove: false };
          let strongMajorityStatus = { hasStrongMajority: false };
          
          try {
            autoApprovalStatus = await governanceContract.checkAutoApprovalStatus(i);
            
            // Try to get strong majority status if available
            try {
              strongMajorityStatus = await governanceContract.checkStrongMajorityStatus(i);
            } catch (smError) {
              console.log(`Strong majority check not available for proposal ${i}:`, smError.message);
            }
            
            // Store the combined status - avoid toNumber() for large numbers
            newAutoApprovalStatuses[i] = {
              canAutoApprove: autoApprovalStatus.canAutoApprove,
              requiredVotes: autoApprovalStatus.requiredVotes, // Keep as BigNumber
              currentVotes: autoApprovalStatus.currentVotes,   // Keep as BigNumber
              threshold: safeNumberConversion(autoApprovalStatus.threshold),
              hasStrongMajority: strongMajorityStatus.hasStrongMajority || false,
              approvalPercentage: safeNumberConversion(strongMajorityStatus.approvalPercentage)
            };
          } catch (statusError) {
            console.error(`Error checking auto-approval status for proposal ${i}:`, statusError.message);
          }
          
          // Add to proposal list - keeping BigNumber values as is
          proposalList.push({
            id: i,
            proposer: proposal.proposer,
            proposalType: proposal.proposalType,
            description: proposal.description,
            creationTime,
            votingEndTime,
            votesFor: proposal.votesFor,
            votesAgainst: proposal.votesAgainst,
            executed: proposal.executed,
            canceled: proposal.canceled,
            canExecute: proposal.canExecute,
            // User-specific data
            hasVoted,
            userSupport: support,
            userWeight: weight, // Keep as BigNumber
            // Auto-approval data
            canAutoApprove: autoApprovalStatus.canAutoApprove,
            hasStrongMajority: strongMajorityStatus.hasStrongMajority || false
          });
        } catch (error) {
          console.error(`Error loading proposal ${i}:`, error);
        }
      }
      
      // Sort by creation time (newest first)
      proposalList.sort((a, b) => b.creationTime - a.creationTime);
      
      // Set the list of all proposals
      setProposals(proposalList);
      
      // CRITICAL FIX: Use the current date for comparison, not a new Date() instance
      // Separate active and past proposals
      const active = proposalList.filter(p => {
        // Log each decision for debugging
        const isExecuted = p.executed;
        const isCanceled = p.canceled;
        const isVotingEnded = currentDate > p.votingEndTime;
        
        console.log(`Proposal #${p.id} classification:`);
        console.log(`- Executed: ${isExecuted}`);
        console.log(`- Canceled: ${isCanceled}`);
        console.log(`- Voting ended: ${isVotingEnded}`);
        console.log(`- Active: ${!isExecuted && !isCanceled && !isVotingEnded}`);
        
        return !isExecuted && !isCanceled && !isVotingEnded;
      });
      
      const past = proposalList.filter(p => 
        p.executed || p.canceled || currentDate > p.votingEndTime
      );
      
      console.log(`Found ${active.length} active proposals and ${past.length} past proposals`);
      console.log('Active proposals:', active);
      console.log('Past proposals:', past);
      
      setActiveProposals(active);
      setPastProposals(past);
      setAutoApprovalStatuses(newAutoApprovalStatuses);
      
    } catch (error) {
      console.error('Error loading proposals:', error);
      setError('Failed to load proposals. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  // Load user's governance data
  const loadUserGovernanceData = async () => {
    try {
      // Get YILDX token balance
      const balance = await yildxTokenContract.balanceOf(account);
      setYildxBalance(ethers.utils.formatEther(balance));
      
      // Get voting power
      const votingPower = await governanceContract.getVotingPower(account);
      setVotingPower(ethers.utils.formatEther(votingPower));
      
    } catch (error) {
      console.error('Error loading user governance data:', error);
    }
  };
  
  // Vote on a proposal
  const handleVote = async (proposalId, support) => {
    setLoading(true);
    
    try {
      // Reset transaction popup state
      setTxStatus('pending');
      setTxHash('');
      setTxMessage(`Your vote (${support ? 'For' : 'Against'}) is being processed on the blockchain.`);
      setShowTxPopup(true);
      
      const tx = await governanceContract.castVote(proposalId, support);
      setTxHash(tx.hash);
      
      await tx.wait();
      
      // Update status
      setTxStatus('success');
      setTxMessage(`Successfully voted ${support ? 'for' : 'against'} proposal #${proposalId}`);
      setSuccess(`Successfully voted ${support ? 'for' : 'against'} proposal #${proposalId}`);
      
      // Use enhanced reload function instead of simple refresh
      await reloadProposals();
      
    } catch (error) {
      console.error(`Error voting on proposal #${proposalId}:`, error);
      
      // Check if the error is due to user rejecting the transaction
      if (error.code === 4001 || (error.message && error.message.includes('user rejected'))) {
        setTxStatus('cancelled');
        setTxMessage('You cancelled the transaction.');
      } else {
        setTxStatus('failed');
        setTxMessage(`Failed to vote: ${error.message}`);
        setError(error.message || 'Failed to vote on proposal');
      }
    } finally {
      setLoading(false);
    }
  };
  
  // Execute a proposal
  const handleExecuteProposal = async (proposalId) => {
    setLoading(true);
    
    try {
      // Reset transaction popup state
      setTxStatus('pending');
      setTxHash('');
      setTxMessage(`Executing proposal #${proposalId}. This transaction is being processed on the blockchain.`);
      setShowTxPopup(true);
      
      const tx = await governanceContract.executeProposal(proposalId);
      setTxHash(tx.hash);
      
      await tx.wait();
      
      // Update status
      setTxStatus('success');
      setTxMessage(`Successfully executed proposal #${proposalId}`);
      setSuccess(`Successfully executed proposal #${proposalId}`);
      
      // Use enhanced reload function
      await reloadProposals();
      
      // Close detail modal if open
      if (showProposalDetails && selectedProposal && selectedProposal.id === proposalId) {
        setShowProposalDetails(false);
      }
      
    } catch (error) {
      console.error(`Error executing proposal #${proposalId}:`, error);
      
      // Check if the error is due to user rejecting the transaction
      if (error.code === 4001 || (error.message && error.message.includes('user rejected'))) {
        setTxStatus('cancelled');
        setTxMessage('You cancelled the transaction.');
      } else {
        setTxStatus('failed');
        setTxMessage(`Failed to execute proposal: ${error.message}`);
        setError(error.message || 'Failed to execute proposal');
      }
    } finally {
      setLoading(false);
    }
  };
  
  // Manually trigger auto-approval check
  const handleTriggerAutoApproval = async (proposalId) => {
    setLoading(true);
    
    try {
      // Reset transaction popup state
      setTxStatus('pending');
      setTxHash('');
      setTxMessage(`Triggering auto-approval check for proposal #${proposalId}.`);
      setShowTxPopup(true);
      
      const tx = await governanceContract.triggerAutoApprovalCheck(proposalId);
      setTxHash(tx.hash);
      
      await tx.wait();
      
      // Update status
      setTxStatus('success');
      setTxMessage(`Auto-approval check triggered for proposal #${proposalId}`);
      setSuccess(`Auto-approval check triggered for proposal #${proposalId}`);
      
      // Use enhanced reload function
      await reloadProposals();
      
    } catch (error) {
      console.error(`Error triggering auto-approval for proposal #${proposalId}:`, error);
      
      // Check if the error is due to user rejecting the transaction
      if (error.code === 4001 || (error.message && error.message.includes('user rejected'))) {
        setTxStatus('cancelled');
        setTxMessage('You cancelled the transaction.');
      } else {
        setTxStatus('failed');
        setTxMessage(`Failed to trigger auto-approval check: ${error.message}`);
        setError(error.message || 'Failed to trigger auto-approval check');
      }
    } finally {
      setLoading(false);
    }
  };
  
  // Cancel a proposal
  const handleCancelProposal = async (proposalId) => {
    setLoading(true);
    
    try {
      // Reset transaction popup state
      setTxStatus('pending');
      setTxHash('');
      setTxMessage(`Cancelling proposal #${proposalId}.`);
      setShowTxPopup(true);
      
      const tx = await governanceContract.cancelProposal(proposalId, "Manually cancelled by proposer");
      setTxHash(tx.hash);
      
      await tx.wait();
      
      // Update status
      setTxStatus('success');
      setTxMessage(`Successfully canceled proposal #${proposalId}`);
      setSuccess(`Successfully canceled proposal #${proposalId}`);
      
      // Use enhanced reload function 
      await reloadProposals();
      
      // Close detail modal if open
      if (showProposalDetails && selectedProposal && selectedProposal.id === proposalId) {
        setShowProposalDetails(false);
      }
      
    } catch (error) {
      console.error(`Error canceling proposal #${proposalId}:`, error);
      
      // Check if the error is due to user rejecting the transaction
      if (error.code === 4001 || (error.message && error.message.includes('user rejected'))) {
        setTxStatus('cancelled');
        setTxMessage('You cancelled the transaction.');
      } else {
        setTxStatus('failed');
        setTxMessage(`Failed to cancel proposal: ${error.message}`);
        setError(error.message || 'Failed to cancel proposal');
      }
    } finally {
      setLoading(false);
    }
  };
  
  // Format a timestamp for display
  const formatTime = (date) => {
    return date.toLocaleString();
  };
  
  // Get proposal type as string
  const getProposalTypeString = (type) => {
    const types = [
      'Change Management Fee',
      'Change Entry Fee',
      'Change Exit Fee',
      'Add Token',
      'Remove Token',
      'Update Token Allocation',
      'Change Slippage Tolerance',
      'Add Operator',
      'Remove Operator',
      'Emergency Action',
      'Misc Admin Action'
    ];
    
    return types[type] || 'Unknown';
  };
  
  // Show proposal details modal
  const showProposalModal = (proposal) => {
    setSelectedProposal(proposal);
    setShowProposalDetails(true);
  };
  
  // Hide proposal details modal
  const hideProposalModal = () => {
    setShowProposalDetails(false);
    setSelectedProposal(null);
  };
  
  // Format address for display
  const formatAddress = (address) => {
    if (!address) return '';
    return `${address.substring(0, 6)}...${address.substring(address.length - 4)}`;
  };
  
  // Auto-Approval Status Component - updated to handle BigNumber
  const AutoApprovalStatus = ({ proposalId }) => {
    const status = autoApprovalStatuses[proposalId];
    
    if (!status) {
      return <div className="text-gray-500">Loading approval status...</div>;
    }
    
    return (
      <div className="space-y-1">
        <div className="flex justify-between">
          <span>Can auto-approve:</span> 
          <span className={status.canAutoApprove ? "text-green-600 font-medium" : "text-red-600"}>
            {status.canAutoApprove ? "Yes" : "No"}
          </span>
        </div>
        
        <div className="flex justify-between">
          <span>Current votes:</span> 
          <span>
            {status.currentVotes && ethers.utils.formatEther(status.currentVotes)} / 
            {status.requiredVotes && ethers.utils.formatEther(status.requiredVotes)} required 
            ({status.threshold}%)
          </span>
        </div>
        
        {/* Show strong majority status if available */}
        <div className="flex justify-between">
          <span>Strong majority status:</span>
          <span className={status.hasStrongMajority ? "text-green-600 font-medium" : "text-gray-600"}>
            {status.hasStrongMajority 
              ? "Eligible for immediate execution" 
              : `${status.approvalPercentage}% (below threshold)`}
          </span>
        </div>
      </div>
    );
  };
  
  // Get filtered proposals based on search and filter
  const getFilteredProposals = () => {
    // Important: Check if currentView is set before proceeding
    if (!currentView) {
      console.warn("currentView is not set, defaulting to 'active'");
      setCurrentView('active');
    }
    
    // Safeguard empty arrays
    if (activeProposals.length === 0 && pastProposals.length === 0) {
      console.warn("Both proposal arrays are empty, triggering reload");
      // Schedule a reload but return empty array for now
      setTimeout(() => {
        if (!loading) reloadProposals();
      }, 100);
      return [];
    }
    
    // Get the filtered list based on current view
    const currentViewToUse = currentView || 'active';
    let filtered = currentViewToUse === 'active' ? activeProposals : pastProposals;
    
    // Log what we're working with
    console.log(`getFilteredProposals: currentView=${currentViewToUse}, filtered.length=${filtered.length}`);
    
    // Apply search filter if needed
    if (searchTerm) {
      filtered = filtered.filter(proposal => 
        proposal.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
        proposal.id.toString().includes(searchTerm) ||
        (proposal.proposer && formatAddress(proposal.proposer).toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }
    
    // Apply type filter if needed
    if (filterType !== 'all') {
      filtered = filtered.filter(proposal => 
        proposal.proposalType.toString() === filterType
      );
    }
    
    // Log the final filtered list for debugging
    console.log(`Final filtered proposals: ${filtered.length}`, filtered);
    
    return filtered;
  };
  
  // Popup handlers
  const handleTxPopupClose = () => {
    setShowTxPopup(false);
  };
  
  const handleTxRefresh = async () => {
    await reloadProposals();
    await loadUserGovernanceData();
  };
  
  // Render the votes display component - updated to handle BigNumber
  const renderVotesDisplay = (proposal) => {
    // Convert BigNumber to string values using ethers.utils.formatEther
    const votesFor = ethers.utils.formatEther(proposal.votesFor);
    const votesAgainst = ethers.utils.formatEther(proposal.votesAgainst);
    
    // Calculate total and percentage
    const totalVotes = parseFloat(votesFor) + parseFloat(votesAgainst);
    const forPercentage = totalVotes > 0 ? (parseFloat(votesFor) / totalVotes) * 100 : 0;
    
    return (
      <div className="w-full">
        <div className="flex justify-between text-xs mb-1">
          <span className="text-green-600 font-medium">{votesFor}</span>
          <span className="text-red-600 font-medium">{votesAgainst}</span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2.5">
          <div 
            className="bg-green-600 h-2.5 rounded-full" 
            style={{ width: `${forPercentage}%` }}
          ></div>
        </div>
        <div className="flex justify-between text-xs mt-1">
          <span>For</span>
          <span>Against</span>
        </div>
      </div>
    );
  };
  
  return (
    <div className="container mx-auto px-4 py-8">
      <div className="bg-white rounded-lg shadow-md p-6 mb-8">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6">
          <div>
            <h1 className="text-2xl font-bold">YILDX Governance Portal</h1>
            <p className="text-gray-600 mt-1">View, vote, and execute governance proposals</p>
          </div>
          <div className="mt-4 md:mt-0">
            {account ? (
              <div className="flex flex-col items-end">
                <div className="flex items-center space-x-2">
                  <span className="text-sm text-gray-600">Connected:</span>
                  <span className="font-medium">{formatAddress(account)}</span>
                  <span className="inline-block bg-green-500 rounded-full w-2 h-2"></span>
                </div>
                <div className="text-sm mt-1">
                  <span className="text-gray-600">Voting Power:</span> 
                  <span className="font-medium ml-1">{parseFloat(votingPower).toLocaleString(undefined, {maximumFractionDigits: 2})}</span>
                </div>
                
                {/* Show operator status if applicable */}
                {operatorInfo && operatorInfo.isCurrentUser && (
                  <div className="mt-1 inline-flex items-center justify-center px-2 py-1 text-xs font-bold leading-none text-indigo-100 bg-indigo-700 rounded">
                    Fund Operator
                  </div>
                )}
              </div>
            ) : (
              <button
                onClick={connect}
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                disabled={web3Loading}
              >
                {web3Loading ? 'Connecting...' : 'Connect Wallet'}
              </button>
            )}
          </div>
        </div>
        
        {/* Summary cards */}
        {account && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div className="bg-indigo-50 rounded-lg p-4">
              <div className="text-sm text-indigo-700">YILDX Balance</div>
              <div className="text-xl font-bold">{parseFloat(yildxBalance).toLocaleString(undefined, {maximumFractionDigits: 2})}</div>
              <div className="text-xs text-gray-600 mt-1">
                {parseFloat(yildxBalance) >= 100 ? 'Eligible to create proposals' : 'Need 100+ YILDX to create proposals'}
              </div>
            </div>
            
            <div className="bg-purple-50 rounded-lg p-4">
              <div className="text-sm text-purple-700">Active Proposals</div>
              <div className="text-xl font-bold">{activeProposals.length}</div>
              <div className="text-xs text-gray-600 mt-1">
                {activeProposals.length === 0 ? 'No active proposals' : 
                 activeProposals.length === 1 ? '1 proposal needs your vote' : 
                 `${activeProposals.length} proposals need your vote`}
              </div>
            </div>
            
            <div className="bg-amber-50 rounded-lg p-4">
              <div className="text-sm text-amber-700">Delegation Status</div>
              <div className="text-xl font-bold">
                {delegatedTo ? 'Delegated' : delegators.length > 0 ? `${delegators.length} Delegators` : 'None'}
              </div>
              <div className="text-xs text-gray-600 mt-1">
                {delegatedTo ? 'You delegated your voting power' : 
                 delegators.length > 0 ? 'Users have delegated to you' : 
                 'No active delegations'}
              </div>
            </div>
          </div>
        )}
        
        {/* Success and error alerts */}
        {success && (
          <div className="bg-green-100 border-l-4 border-green-500 text-green-700 p-4 mb-4 rounded" role="alert">
            <p>{success}</p>
            <button 
              className="absolute top-0 right-0 mt-4 mr-4 text-green-700"
              onClick={() => setSuccess(null)}
            >
              ×
            </button>
          </div>
        )}
        
        {error && (
          <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 mb-4 rounded" role="alert">
            <p>{error}</p>
            <button 
              className="absolute top-0 right-0 mt-4 mr-4 text-red-700"
              onClick={() => setError(null)}
            >
              ×
            </button>
          </div>
        )}
        
        {/* Tab navigation */}
        <div className="border-b border-gray-200 mb-6">
          <nav className="-mb-px flex">
            <button
              className={`py-4 px-6 text-center border-b-2 font-medium text-sm ${
                activeTab === 'active'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
              onClick={() => {
                console.log("Switching to active tab");
                setActiveTab('active');
                setCurrentView('active');
                console.log("Active proposals to display:", activeProposals.length);
              }}
            >
              Active Proposals ({activeProposals.length})
            </button>
            <button
              className={`py-4 px-6 text-center border-b-2 font-medium text-sm ${
                activeTab === 'past'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
              onClick={() => {
                console.log("Switching to past tab");
                setActiveTab('past');
                setCurrentView('past');
                console.log("Past proposals to display:", pastProposals.length);
              }}
            >
              Past Proposals ({pastProposals.length})
            </button>
          </nav>
        </div>
        
        {/* Search and filters */}
        <div className="flex flex-col md:flex-row gap-4 mb-6">
          <div className="md:w-2/3">
            <input
              type="text"
              placeholder="Search proposals by description, ID, or proposer..."
              className="w-full p-3 border border-gray-300 rounded-lg"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div className="md:w-1/3">
            <select
              className="w-full p-3 border border-gray-300 rounded-lg"
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
            >
              <option value="all">All Proposal Types</option>
              <option value="0">Change Management Fee</option>
              <option value="1">Change Entry Fee</option>
              <option value="2">Change Exit Fee</option>
              <option value="3">Add Token</option>
              <option value="4">Remove Token</option>
              <option value="5">Update Token Allocation</option>
              <option value="6">Change Slippage Tolerance</option>
              <option value="7">Add Operator</option>
              <option value="8">Remove Operator</option>
              <option value="9">Emergency Action</option>
              <option value="10">Misc Admin Action</option>
            </select>
          </div>
        </div>
        
        {/* Proposals List */}
        {loading ? (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
          </div>
        ) : (
          <>
            {/* Debug display for current view and proposals (comment out in production) */}
            {/* <div className="bg-gray-100 p-3 mb-4 text-xs">
              <p>Current View: {currentView}</p>
              <p>Active Proposals: {activeProposals.length}</p>
              <p>Past Proposals: {pastProposals.length}</p>
              <p>Filtered Proposals: {getFilteredProposals().length}</p>
            </div> */}
            
            {getFilteredProposals().length > 0 ? (
              <div className="overflow-x-auto">
                <table className="min-w-full bg-white border border-gray-200 rounded-lg">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ID</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Votes</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {getFilteredProposals().map(proposal => (
                      <tr 
                        key={proposal.id} 
                        className="hover:bg-gray-50 cursor-pointer"
                        onClick={() => showProposalModal(proposal)}
                      >
                        <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {proposal.id}
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-600">
                          {getProposalTypeString(proposal.proposalType)}
                        </td>
                        <td className="px-4 py-4 text-sm text-gray-600">
                          <div className="max-w-xs truncate">
                            {proposal.description}
                          </div>
                          <div className="text-xs text-gray-500 mt-1">
                            {formatTime(proposal.creationTime)}
                          </div>
                        </td>
                        <td className="px-4 py-4 text-sm text-gray-600">
                          {renderVotesDisplay(proposal)}
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm">
                          {proposal.executed ? (
                            <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                              Executed
                            </span>
                          ) : proposal.canceled ? (
                            <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-red-100 text-red-800">
                              Canceled
                            </span>
                          ) : new Date() > proposal.votingEndTime ? (
                            proposal.canExecute ? (
                              <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">
                                Ready to Execute
                              </span>
                            ) : (
                              <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-yellow-100 text-yellow-800">
                                Voting Ended
                              </span>
                            )
                          ) : proposal.hasStrongMajority ? (
                            <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-indigo-100 text-indigo-800 items-center">
                              <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                              </svg>
                              Strong Majority
                            </span>
                          ) : (
                            <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-purple-100 text-purple-800">
                              Voting Active
                            </span>
                          )}
                          
                          {/* Show user vote status if they voted */}
                          {proposal.hasVoted && (
                            <span className={`ml-2 px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full
                              ${proposal.userSupport 
                                ? 'bg-green-50 text-green-700' 
                                : 'bg-red-50 text-red-700'}`}
                            >
                              You voted {proposal.userSupport ? 'For' : 'Against'}
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-600">
                          <button
                            className="text-blue-600 hover:text-blue-900 mr-2"
                            onClick={(e) => {
                              e.stopPropagation(); // Prevent row click
                              showProposalModal(proposal);
                            }}
                          >
                            View Details
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-12 bg-gray-50 rounded-lg">
                <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <h3 className="mt-2 text-sm font-medium text-gray-900">No proposals found</h3>
                <p className="mt-1 text-sm text-gray-500">
                  {searchTerm || filterType !== 'all' 
                    ? 'Try changing your search or filter criteria'
                    : currentView === 'active' 
                      ? 'There are no active proposals at this time' 
                      : 'There are no past proposals to display'}
                </p>
              </div>
            )}
          </>
        )}
        
        {/* Action buttons */}
        <div className="mt-6 flex justify-between">
          <div>
            <a 
              href="/delegation"
              className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Return to Delegation Dashboard
            </a>
          </div>
          <div>
            <button
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              onClick={reloadProposals}
              disabled={loading}
            >
              {loading ? 'Refreshing...' : 'Refresh Proposals'}
            </button>
          </div>
        </div>
      </div>
      
      {/* Information section */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-8">
        <h2 className="text-xl font-bold mb-4">About YILDX Governance</h2>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-blue-50 rounded-lg p-4">
            <h3 className="font-semibold text-blue-800 mb-2">Voting Power</h3>
            <p className="text-sm text-gray-700">
              Your voting power is determined by your YILDX token balance. Each token equals one vote.
              You can delegate your voting power to other addresses while keeping your tokens.
            </p>
          </div>
          
          <div className="bg-amber-50 rounded-lg p-4">
            <h3 className="font-semibold text-amber-800 mb-2">Operators</h3>
            <p className="text-sm text-gray-700">
              Operators have special privileges to manage the fund, but only within the boundaries established by governance voting. 
              They can perform day-to-day operations such as rebalancing the portfolio according to approved allocations, 
              distributing ETH, and executing trades that follow the fund's set parameters.
            </p>
          </div>
          
          <div className="bg-indigo-50 rounded-lg p-4">
            <h3 className="font-semibold text-indigo-800 mb-2">Auto-Approval</h3>
            <p className="text-sm text-gray-700">
              Proposals may be automatically executed when they reach sufficient votes. Regular proposals need a majority vote and a waiting period, while proposals with a strong majority can be executed immediately without waiting.
            </p>
          </div>
        </div>
        
        <div className="mt-6 bg-gray-50 rounded-lg p-4">
          <h3 className="font-semibold mb-2">Governance Rules</h3>
          <ul className="list-disc list-inside text-sm text-gray-700 space-y-1">
            <li>Creating a proposal requires at least 100 YILDX tokens</li>
            <li>Proposals have a voting period, followed by an execution delay</li>
            <li>A proposal passes if it has more 'For' votes than 'Against' votes</li>
            <li>Sensitive proposals (like adding/removing operators) may have higher thresholds</li>
            <li>You can delegate your voting power while keeping your tokens</li>
            <li>You can only vote once per proposal</li>
            <li>Proposals cannot be changed after creation, but can be canceled by their proposer</li>
          </ul>
        </div>
      </div>
      
      {/* Proposal Details Modal */}
      {showProposalDetails && selectedProposal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-screen overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-start">
                <h3 className="text-xl font-bold">Proposal #{selectedProposal.id}</h3>
                <button 
                  className="text-gray-400 hover:text-gray-500"
                  onClick={hideProposalModal}
                >
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              
              <div className="mt-4 space-y-4">
                <div className="bg-gray-50 p-4 rounded-lg">
                  <div className="text-lg font-medium mb-2">{selectedProposal.description}</div>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-gray-600">Type:</span> {getProposalTypeString(selectedProposal.proposalType)}
                    </div>
                    <div>
                      <span className="text-gray-600">Proposer:</span> {formatAddress(selectedProposal.proposer)}
                    </div>
                    <div>
                      <span className="text-gray-600">Created:</span> {formatTime(selectedProposal.creationTime)}
                    </div>
                    <div>
                      <span className="text-gray-600">Voting Ends:</span> {formatTime(selectedProposal.votingEndTime)}
                    </div>
                  </div>
                </div>
                
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-medium mb-2">Voting Results</h4>
                  {renderVotesDisplay(selectedProposal)}
                  
                  <div className="mt-4 grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-gray-600">Votes For:</span> {ethers.utils.formatEther(selectedProposal.votesFor)}
                    </div>
                    <div>
                      <span className="text-gray-600">Votes Against:</span> {ethers.utils.formatEther(selectedProposal.votesAgainst)}
                    </div>
                    <div>
                      <span className="text-gray-600">Status:</span>{' '}
                      {selectedProposal.executed ? "Executed" :
                      selectedProposal.canceled ? "Canceled" :
                      new Date() > selectedProposal.votingEndTime ? (
                        selectedProposal.canExecute ? "Ready to Execute" : "Voting Ended"
                      ) : selectedProposal.hasStrongMajority ? "Strong Majority Reached" : "Voting Active"}
                    </div>
                    <div>
                      <span className="text-gray-600">Your Vote:</span>{' '}
                      {selectedProposal.hasVoted ? 
                        (selectedProposal.userSupport ? "Voted For" : "Voted Against") : 
                        "Not Voted"}
                    </div>
                  </div>
                </div>
                
                {/* Auto-Approval Status Section */}
                {!selectedProposal.executed && !selectedProposal.canceled && (
                  <div className="bg-indigo-50 p-4 rounded-lg">
                    <h4 className="font-medium mb-2 text-indigo-800">Auto-Approval Status</h4>
                    <AutoApprovalStatus proposalId={selectedProposal.id} />
                    
                    {/* Add manual trigger button */}
                    <button
                      className="mt-3 w-full px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:bg-gray-400 text-sm"
                      onClick={() => handleTriggerAutoApproval(selectedProposal.id)}
                      disabled={loading || selectedProposal.executed || selectedProposal.canceled}
                    >
                      {loading ? "Processing..." : "Manually Trigger Auto-Approval Check"}
                    </button>
                    <div className="text-xs text-indigo-600 mt-1">
                      This will check if the proposal has enough votes for automatic execution.
                    </div>
                  </div>
                )}
                
                {/* Action Buttons */}
                <div className="flex justify-between pt-4">
                  <div>
                    {/* Voting buttons */}
                    {!selectedProposal.executed && !selectedProposal.canceled && new Date() <= selectedProposal.votingEndTime && !selectedProposal.hasVoted && (
                      <div className="space-x-2">
                        <button
                          className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:bg-gray-400"
                          onClick={() => handleVote(selectedProposal.id, true)}
                          disabled={loading}
                        >
                          Vote For
                        </button>
                        <button
                          className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:bg-gray-400"
                          onClick={() => handleVote(selectedProposal.id, false)}
                          disabled={loading}
                        >
                          Vote Against
                        </button>
                      </div>
                    )}
                    
                    {/* Proposer cancel button */}
                    {!selectedProposal.executed && !selectedProposal.canceled && (account && account.toLowerCase() === selectedProposal.proposer.toLowerCase()) && (
                      <button
                        className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:bg-gray-400"
                        onClick={() => handleCancelProposal(selectedProposal.id)}
                        disabled={loading}
                      >
                        Cancel Proposal
                      </button>
                    )}
                  </div>
                  
                  <div>
                    {/* Execute button */}
                    {selectedProposal.canExecute && (
                      <button
                        className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-400"
                        onClick={() => handleExecuteProposal(selectedProposal.id)}
                        disabled={loading}
                      >
                        Execute Proposal
                      </button>
                    )}
                    
                    <button
                      className="ml-2 px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300"
                      onClick={hideProposalModal}
                    >
                      Close
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Transaction Result Popup */}
      <TransactionResultPopup
        isOpen={showTxPopup}
        onClose={handleTxPopupClose}
        onRefresh={handleTxRefresh}
        status={txStatus}
        actionType="Governance Action"
        message={txMessage}
        txHash={txHash}
        provider={txProvider}
      />
    </div>
  );
};

export default GovernancePortalPage;